import { z } from 'zod';
import { authSchema } from './auth.js';

/**
 * Connection Category Enum
 */
export const connectionCategorySchema = z.enum([
  'SEMANTIC_LAYER',
  'CRM',
  'DATABASE',
  'API',
  'DOCUMENT_STORE',
  'MCP_SERVER',
]);
export type ConnectionCategory = z.infer<typeof connectionCategorySchema>;

/**
 * Connection Provider Enum
 */
export const connectionProviderSchema = z.enum([
  'CUBEJS',
  'SALESFORCE',
  'POSTGRES',
  'OPENSEARCH',
  'SPECIFIC_HOST',
  'SHAREPOINT',
  'VEEVAVAULT',
  'MCP',
]);
export type ConnectionProvider = z.infer<typeof connectionProviderSchema>;

/**
 * MCP Server Connection Config
 */
export const mcpServerConnectionConfigSchema = z.object({
  // url: z.url().refine(
  //   (u) => new URL(u).protocol === 'https:',
  //   { message: 'MCP server URL must use HTTPS' }
  // ),
  url: z.url(),
  auth: authSchema,
});
export type McpServerConnectionConfig = z.infer<typeof mcpServerConnectionConfigSchema>;

/**
 * API Server Connection Config
 */
export const apiServerConnectionConfigSchema = z.object({
  url: z.url(),
  auth: authSchema,
});
export type ApiServerConnectionConfig = z.infer<typeof apiServerConnectionConfigSchema>;

/**
 * Cube Dev Connection Config
 */
export const cubeDevConnectionConfigSchema = z.object({
  host: z.url(),
  tokenSecretIdentifier: z.string().min(1),
});
export type CubeDevConnectionConfig = z.infer<typeof cubeDevConnectionConfigSchema>;

/**
 * OpenSearch Connection Config
 */
export const openSearchConnectionConfigSchema = z.object({
  host: z.url(),
  username: z.string().min(1),
  passwordSecretIdentifier: z.string().min(1),
});
export type OpenSearchConnectionConfig = z.infer<typeof openSearchConnectionConfigSchema>;

/**
 * Postgres Connection Config
 */
export const postgresConnectionConfigSchema = z.object({
  host: z.string().min(1),
  port: z.number().int().min(1).max(65535),
  databaseName: z.string().min(1),
  username: z.string().min(1),
  ssl: z.boolean(),
  passwordSecretIdentifier: z.string().min(1),
});
export type PostgresConnectionConfig = z.infer<typeof postgresConnectionConfigSchema>;

/**
 * Connection Status Enum
 */
export const connectionStatusSchema = z.enum(['active', 'error', 'pending_test', 'disabled']);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/**
 * MCP tool metadata returned from GET /data-connections/:identifier/mcp-tools
 */
export const mcpToolMetadataSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  inputSchema: z
    .looseObject({
      type: z.literal('object'),
      properties: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
});
export type McpToolMetadata = z.infer<typeof mcpToolMetadataSchema>;

export const listMcpToolsResponseSchema = z.object({
  tools: z.array(mcpToolMetadataSchema),
});
export type ListMcpToolsResponse = z.infer<typeof listMcpToolsResponseSchema>;

/**
 * Test Result Schema
 * Handles both legacy format ({ success: boolean }) and current format ({ status: 'success' | 'error' })
 */
export const testResultSchema = z
  .object({
    success: z.boolean().optional(),
    status: z.enum(['success', 'error']).optional(),
    message: z.string(),
    timestamp: z.coerce.date(),
  })
  .transform((data) => ({
    status: (data.status ?? (data.success ? 'success' : 'error')),
    message: data.message,
    timestamp: data.timestamp,
  }))
  .nullable()
  .optional();
export type TestResult = z.infer<typeof testResultSchema>;

/**
 * Citation lookup metadata schema
 */
export const dataConnectionCitationLookupFieldsSchema = z.object({
  lookupIdentifier: z.string().min(1),
  lastRefreshDate: z.string().min(1),
  maxMaturityDate: z.string().min(1),
  sourceTables: z.string().min(1),
});
export type DataConnectionCitationLookupFields = z.infer<typeof dataConnectionCitationLookupFieldsSchema>;

export const dataConnectionCitationMetadataSchema = z.object({
  dataConnectionIdentifier: z.string().min(1),
  sourceTable: z.string().trim().min(1),
  fields: dataConnectionCitationLookupFieldsSchema,
});
export type DataConnectionCitationMetadata = z.infer<typeof dataConnectionCitationMetadataSchema>;

/**
 * Create Data Connection Request Body
 */
const providerValidationRules: Partial<Record<string, { schema: z.ZodTypeAny; configMessage: string; secretsMessage: string }>> = {
  SPECIFIC_HOST: {
    schema: apiServerConnectionConfigSchema,
    configMessage: 'SPECIFIC_HOST config must include url and auth',
    secretsMessage: 'SPECIFIC_HOST auth must be configured via config.auth',
  },
  CUBEJS: {
    schema: cubeDevConnectionConfigSchema,
    configMessage: 'CUBEJS config must include host and tokenSecretIdentifier',
    secretsMessage: 'CUBEJS token must be configured via managed secret identifier in config',
  },
  OPENSEARCH: {
    schema: openSearchConnectionConfigSchema,
    configMessage: 'OPENSEARCH config must include host, username, and passwordSecretIdentifier',
    secretsMessage: 'OPENSEARCH password must be configured via managed secret identifier in config',
  },
  POSTGRES: {
    schema: postgresConnectionConfigSchema,
    configMessage: 'POSTGRES config must include host, port, databaseName, username, ssl, and passwordSecretIdentifier',
    secretsMessage: 'POSTGRES password must be configured via managed secret identifier in config',
  },
};

export const createDataConnectionBodySchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    connectionCategory: connectionCategorySchema,
    connectionProvider: connectionProviderSchema,
    config: z.record(z.string(), z.unknown()).default({}),
    /**
     * Secrets for the data connection (Key-Value pairs)
     * - Omitted: No secret stored in AWS Secrets Manager
     * - Empty object `{}`: Equivalent to omitted (no secret)
     * - Non-empty `{key: "value", ...}`: Creates a secret in AWS Secrets Manager
     *
     * Example: `{ password: "db-password", apiKey: "sk-123456" }`
     */
    secrets: z.record(z.string(), z.string()).optional(),
    citationMetadata: dataConnectionCitationMetadataSchema.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const rule = providerValidationRules[data.connectionProvider];
    if (!rule) return;

    const configResult = rule.schema.safeParse(data.config);
    if (!configResult.success) {
      ctx.addIssue({ code: 'custom', path: ['config'], message: rule.configMessage });
    }
    if (data.secrets !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['secrets'], message: rule.secretsMessage });
    }
  });
export type CreateDataConnectionBody = z.infer<typeof createDataConnectionBodySchema>;

/**
 * Update Data Connection Request Body
 */
export const updateDataConnectionBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  /**
   * Secrets for the data connection (Key-Value pairs)
   * - Omitted: No change to existing secrets
   * - Empty object `{}`: Delete existing secret from AWS Secrets Manager
   * - Non-empty `{key: "value", ...}`: Create or update secret in AWS Secrets Manager
   *
   * Example: `{ password: "new-password", apiKey: "sk-987654" }`
   */
  secrets: z.record(z.string(), z.string()).optional(),
  citationMetadata: dataConnectionCitationMetadataSchema.nullable().optional(),
});
export type UpdateDataConnectionBody = z.infer<typeof updateDataConnectionBodySchema>;

/**
 * Data Connection Response (single)
 */
export const dataConnectionResponseSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  connectionCategory: connectionCategorySchema,
  connectionProvider: connectionProviderSchema,
  config: z.record(z.string(), z.unknown()),
  citationMetadata: dataConnectionCitationMetadataSchema.nullable().optional(),
  status: connectionStatusSchema,
  testResult: testResultSchema,
  secretRef: z.string().nullable(),
  lastSyncedAt: z.coerce.date().nullable().optional(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable(),
  updatedBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type DataConnectionResponse = z.infer<typeof dataConnectionResponseSchema>;

/**
 * List Data Connections Response
 */
export const listDataConnectionsResponseSchema = z.object({
  items: z.array(dataConnectionResponseSchema),
});
export type ListDataConnectionsResponse = z.infer<typeof listDataConnectionsResponseSchema>;

/**
 * Test Data Connection Request
 */
export const testDataConnectionRequestSchema = z.object({
  // No body required — test endpoint extracts identifier from URL
});

/**
 * Test Data Connection Response
 */
export const testDataConnectionResponseSchema = z.object({
  status: z.enum(['success', 'error']),
  message: z.string(),
  timestamp: z.coerce.date(),
});
export type TestDataConnectionResponse = z.infer<typeof testDataConnectionResponseSchema>;

/**
 * Test Citation Metadata Lookup Request
 */
export const testCitationMetadataLookupRequestSchema = z.object({
  sourceTable: z.string().trim().min(1),
});
export type TestCitationMetadataLookupRequest = z.infer<typeof testCitationMetadataLookupRequestSchema>;

/**
 * Citation metadata display values returned by adapter lookup
 */
export const citationMetadataDisplayValuesSchema = z.object({
  lastUpdatedOn: z.string().nullable(),
  maxMaturityDate: z.string().nullable(),
  sourceTables: z.string().nullable(),
});
export type CitationMetadataDisplayValues = z.infer<typeof citationMetadataDisplayValuesSchema>;

/**
 * Test Citation Metadata Lookup Response
 */
export const testCitationMetadataLookupResponseSchema = z.object({
  status: z.enum(['success', 'not_found']),
  message: z.string(),
  lookupValue: z.string(),
  displayValues: citationMetadataDisplayValuesSchema,
});
export type TestCitationMetadataLookupResponse = z.infer<typeof testCitationMetadataLookupResponseSchema>;
